import * as PIXI from 'pixi.js';
import { Connector } from '../../entities/Connector';
import { HarnessNode } from '../../entities/HarnessNode';
import { WireBundle } from '../../entities/WireBundle';
import { BundlePreview } from '../../entities/BundlePreview';
import { PreviewNode } from '../../entities/PreviewNode';
import { AppState, WireBundle as WireBundleData, Connector as ConnectorData, HarnessNode as HarnessNodeData } from '../../types';
import { connectorCenter } from '../../utils/geometry';
import { RenderContext } from './types';
import { beginEntityDrag, dragEntityGroup, endEntityDrag } from './groupDrag';

/** Wires up drag and wire-creation events for a newly created connector. */
export function setupConnectorEvents(connector: Connector, connectorData: ConnectorData, ctx: RenderContext): void {
  const { refs, stateManager, contextMenuHandlers } = ctx;

  connector.on('positionChanged', (newX: number, newY: number) => {
    if (refs.mounted.current) {
      stateManager.updateConnector(connectorData.id, { xPosition: newX, yPosition: newY });
      dragEntityGroup(refs, stateManager, connectorData.id, newX, newY);
    }
  });

  connector.on('dragStart', () => {
    stateManager.beginBatch();
    beginEntityDrag(refs, stateManager, connectorData.id);
    refs.interactionManager.current?.setEntityDragging(true);
  });

  connector.on('dragEnd', () => {
    stateManager.endBatch();
    endEntityDrag(refs);
    refs.interactionManager.current?.setEntityDragging(false);
  });

  const startWireFrom = (startPinId: string): void => {
    const source = stateManager.getState().connectors.find(t => t.id === connectorData.id);
    if (!source) {
      return;
    }
    refs.temporaryNodeIds.current = [];
    const center = connectorCenter(source);
    stateManager.setInteractionMode('wireCreation');
    stateManager.setPreviewWire({
      startConnectorId: connectorData.id,
      startPinId,
      currentX: center.x,
      currentY: center.y,
      pathNodes: []
    });
  };

  // A wire starts by clicking a pin in the source gutter; only in normal mode,
  // so clicking a pin on the target connector while creating still completes the
  // wire through the canvas handler instead of starting a second one.
  connector.on('pinPointerDown', (pinId: string) => {
    if (!refs.mounted.current || stateManager.getInteractionState().mode !== 'normal') {
      return;
    }
    startWireFrom(pinId);
  });

  connector.on('addPinPointerDown', () => {
    if (!refs.mounted.current || stateManager.getInteractionState().mode !== 'normal') {
      return;
    }
    const pinId = stateManager.addPin(connectorData.id);
    if (pinId) {
      startWireFrom(pinId);
    }
  });

  connector.on('wireRowHover', (wireId: string) => {
    if (refs.mounted.current) {
      stateManager.setHighlightedWire(wireId);
    }
  });

  connector.on('wireRowHoverEnd', () => {
    if (refs.mounted.current) {
      stateManager.setHighlightedWire(undefined);
    }
  });

  connector.on('wireRowDoubleClick', (wireId: string) => {
    if (refs.mounted.current) {
      contextMenuHandlers.handleWireEdit(wireId);
    }
  });
}

/** Wires up drag events for a newly created harness node. */
export function setupNodeEvents(node: HarnessNode, nodeData: HarnessNodeData, ctx: RenderContext): void {
  const { refs, stateManager } = ctx;

  node.on('positionChanged', (newX: number, newY: number) => {
    if (refs.mounted.current) {
      stateManager.updateNode(nodeData.id, { xPosition: newX, yPosition: newY });
      dragEntityGroup(refs, stateManager, nodeData.id, newX, newY);
    }
  });

  node.on('dragStart', () => {
    stateManager.beginBatch();
    beginEntityDrag(refs, stateManager, nodeData.id);
    refs.interactionManager.current?.setEntityDragging(true);
  });

  node.on('dragEnd', () => {
    stateManager.endBatch();
    endEntityDrag(refs);
    refs.interactionManager.current?.setEntityDragging(false);
  });

  node.on('doubleClick', () => {
    if (refs.mounted.current) {
      stateManager.removeNode(nodeData.id);
    }
  });
}

/** Wires up tooltip and drag-to-add-node events for a wire bundle. */
export function attachBundleEvents(
  wireBundle: WireBundle,
  bundleData: WireBundleData,
  state: AppState,
  viewport: PIXI.Container,
  ctx: RenderContext
): void {
  const { refs, stateManager, contextMenuHandlers, isValidNodePosition, calculateOptimalInsertIndex } = ctx;

  wireBundle.on('sectionDoubleClick', (aId: string, bId: string) => {
    if (refs.mounted.current) {
      contextMenuHandlers.handleSectionEdit(aId, bId);
    }
  });

  wireBundle.on('showTooltip', (wireList: string, globalX: number, globalY: number) => {
    refs.tooltip.current?.show(wireList, globalX, globalY);
  });

  wireBundle.on('hideTooltip', () => {
    refs.tooltip.current?.hide();
  });

  wireBundle.on('bundleDragStart', (bundleId: string, globalX: number, globalY: number) => {
    if (refs.bundleDragState.current || !refs.viewportManager.current) {
      return;
    }

    refs.bundleDragState.current = { bundleId, isDragging: true };
    refs.interactionManager.current?.setEntityDragging(true);

    if (!refs.previewNode.current) {
      refs.previewNode.current = new PreviewNode();
      viewport.addChild(refs.previewNode.current);
    }

    const worldCoords = refs.viewportManager.current.screenToWorld(globalX, globalY);
    refs.previewNode.current.setPosition(worldCoords.x, worldCoords.y);
    refs.previewNode.current.show();

    if (!refs.bundlePreview.current) {
      refs.bundlePreview.current = new BundlePreview(
        bundleData,
        { x: worldCoords.x, y: worldCoords.y },
        state.connectors,
        state.nodes
      );
      viewport.addChild(refs.bundlePreview.current);
    }

    stateManager.setInteractionMode('bundleDrag');
    stateManager.setDragData({
      type: 'bundle',
      wireIds: bundleData.wires.map(w => w.id),
      previewNodePosition: { x: worldCoords.x, y: worldCoords.y }
    });
  });

  wireBundle.on('bundleDragMove', (bundleId: string, globalX: number, globalY: number) => {
    if (!refs.bundleDragState.current || refs.bundleDragState.current.bundleId !== bundleId || !refs.viewportManager.current || !refs.previewNode.current) {
      return;
    }

    const worldCoords = refs.viewportManager.current.screenToWorld(globalX, globalY);
    refs.previewNode.current.setPosition(worldCoords.x, worldCoords.y);
    refs.bundlePreview.current?.updatePreviewPosition(worldCoords.x, worldCoords.y);

    refs.previewNode.current.setValid(isValidNodePosition(worldCoords.x, worldCoords.y));

    stateManager.setDragData({
      type: 'bundle',
      wireIds: bundleData.wires.map(w => w.id),
      previewNodePosition: { x: worldCoords.x, y: worldCoords.y }
    });
  });

  wireBundle.on('bundleDragEnd', (bundleId: string, globalX: number, globalY: number) => {
    if (!refs.bundleDragState.current || refs.bundleDragState.current.bundleId !== bundleId || !refs.viewportManager.current) {
      return;
    }

    const worldCoords = refs.viewportManager.current.screenToWorld(globalX, globalY);

    if (isValidNodePosition(worldCoords.x, worldCoords.y)) {
      const newNode = {
        id: stateManager.generateUniqueId(),
        xPosition: worldCoords.x,
        yPosition: worldCoords.y
      };

      // Adding the node and rerouting every wire through it is one user action.
      stateManager.beginBatch();
      stateManager.addNode(newNode);

      bundleData.wires.forEach(wire => {
        const insertIndex = calculateOptimalInsertIndex(wire, worldCoords.x, worldCoords.y);
        stateManager.insertNodeInWirePath(wire.id, newNode.id, insertIndex);
      });
      stateManager.endBatch();
    }

    if (refs.previewNode.current) {
      viewport.removeChild(refs.previewNode.current);
      refs.previewNode.current = null;
    }

    if (refs.bundlePreview.current) {
      viewport.removeChild(refs.bundlePreview.current);
      refs.bundlePreview.current = null;
    }

    refs.bundleDragState.current = null;
    stateManager.setInteractionMode('normal');
    stateManager.setDragData(undefined);
    refs.interactionManager.current?.setEntityDragging(false);
  });
}
